import React from 'react';
import { Check, Clock, Car, ChevronRight } from 'lucide-react';
import { SERVICES, VEHICLE_TYPES, type Service } from './bookingTypes';

interface Step1Props {
  selected: Service | null;
  vehicleType: string;
  onVehicleChange: (v: string) => void;
  onSelect: (s: Service) => void;
  onNext: () => void;
}

export default function Step1Services({ selected, vehicleType, onVehicleChange, onSelect, onNext }: Step1Props) {
  const filteredServices = SERVICES.filter((s) => s.vehicleTypes.includes(vehicleType));

  return (
    <div className="animate-slide-up">
      {/* Vehicle Type Selector */}
      <div className="bg-card rounded-xl border border-border p-5 mb-6 shadow-card">
        <div className="flex items-center gap-2 mb-3">
          <Car size={16} className="text-primary" />
          <h3 className="text-sm font-600 text-foreground">What type of vehicle?</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {VEHICLE_TYPES.map((v) => (
            <button
              key={`vtype-${v.id}`}
              onClick={() => onVehicleChange(v.id)}
              className={`px-4 py-2 rounded-lg text-sm font-500 border transition-all ${
                vehicleType === v.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-foreground border-border hover:border-primary hover:text-primary'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Service Cards */}
      <h3 className="text-base font-600 text-foreground mb-4">
        Available Services
        <span className="text-muted-foreground font-400 text-sm ml-2">({filteredServices.length} services)</span>
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        {filteredServices.map((service) => {
          const isSelected = selected?.id === service.id;
          return (
            <button
              key={service.id}
              onClick={() => onSelect(service)}
              className={`text-left bg-card rounded-xl border p-5 card-hover transition-all cursor-pointer group relative ${
                isSelected ? 'service-card-selected border-primary' : 'border-border'
              }`}
            >
              {/* Badge */}
              {service.badge && (
                <span className={`text-[11px] font-600 px-2 py-0.5 rounded-full ${service.badgeColor} mb-3 inline-block`}>
                  {service.badge}
                </span>
              )}
              {!service.badge && <div className="h-6 mb-3" />}

              {/* Icon + Name */}
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="text-2xl leading-none">{service.icon}</span>
                  <h4 className="text-sm font-700 text-foreground mt-2">{service.name}</h4>
                </div>
                {isSelected && (
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                    <Check size={12} className="text-primary-foreground" strokeWidth={3} />
                  </div>
                )}
              </div>

              {/* Description */}
              <p className="text-xs text-muted-foreground leading-relaxed mb-4">{service.description}</p>

              {/* Footer */}
              <div className="flex items-center justify-between mt-auto">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Clock size={12} />
                  <span className="text-xs font-500">
                    {service.duration >= 60 ? `${service.duration / 60}h` : `${service.duration}m`}
                    {service.duration % 60 !== 0 && service.duration > 60 ? ` ${service.duration % 60}m` : ''}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-lg font-700 text-foreground tabular-nums">${service.price}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Next Button */}
      <div className="flex justify-end">
        <button
          onClick={onNext}
          disabled={!selected}
          className="btn-primary px-6 py-2.5 flex items-center gap-2"
        >
          Continue to Date & Time
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}